"use client";

import React from "react";
import { Search, LayoutGrid, List, Orbit, X } from "lucide-react";
import { motion } from "framer-motion";

const categories = ["All", "Manufacturing", "Healthcare", "Trading", "Services", "Construction"];

interface IndustriesFilterBarProps {
  search: string;
  setSearch: (val: string) => void;
  category: string;
  setCategory: (val: string) => void;
  viewMode: "grid" | "list" | "orbital";
  setViewMode: (val: "grid" | "list" | "orbital") => void;
  resultCount: number;
  isSticky: boolean;
}

export function IndustriesFilterBar({
  search,
  setSearch,
  category,
  setCategory,
  viewMode,
  setViewMode,
  resultCount,
  isSticky
}: IndustriesFilterBarProps) {
  return (
    <div 
      className={`z-40 transition-all duration-300 ${
        isSticky 
          ? "sticky top-[var(--header-height)] bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-lg shadow-slate-900/5 py-4" 
          : "py-6 bg-transparent"
      }`}
    >
      <div className="container-site">
        <div className="flex flex-col lg:flex-row items-center gap-6">
          
          {/* Search */}
          <div className="relative w-full lg:w-72 shrink-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search industries..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
            {search && (
              <button 
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 rounded-full text-slate-400"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Categories */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 lg:pb-0 w-full">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat.toLowerCase())}
                className={`px-5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                  category === cat.toLowerCase()
                    ? "bg-primary text-white shadow-lg shadow-primary/20 scale-105"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* View Toggles & Count */}
          <div className="flex items-center gap-4 ml-auto shrink-0">
            <div className="hidden sm:flex items-center gap-1.5 p-1 bg-slate-100 rounded-xl">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === "grid" ? "bg-white text-primary shadow-sm" : "text-slate-400 hover:text-slate-600"
                }`}
                title="Grid View"
              >
                <LayoutGrid size={18} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === "list" ? "bg-white text-primary shadow-sm" : "text-slate-400 hover:text-slate-600"
                }`}
                title="List View"
              >
                <List size={18} />
              </button>
            </div>
            
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-3 py-2 rounded-lg border border-slate-100">
              {resultCount} Industries
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
